import React, { useEffect, useMemo, useState } from "react";
import {
    Grid,
    List,
    ListItemButton,
    ListItemText,
    Collapse,
    Chip,
    Stack,
    Button,
    Container,
} from "@mui/material";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import { useHistory, useParams } from "react-router-dom";
import { useCmrAuth } from "state/CMRAuth";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CloseIcon from "@mui/icons-material/Close";
import RoutesName from "routes/routesName";
import Header from "../../components/AppHeader";
import Footer from "../../components/AppFooter";
import "./CategoryPage.scss";
import "./../preset/PresetPage.scss";
import CategoryBreadcrumbs from "../../components/category/CategoryBreadcrumbs";
import SetupCard from "ui/components/preset/SetupCard";
import CategoryCard from "ui/components/category/CategoryCard";

type Category = {
    id: number;
    name: string;
    slug: string;
    totalSceneCount: number;
    directSceneCount: number;
    children: Category[];
};

const CategoryPage = (): JSX.Element => {
    const history = useHistory();
    const { slug = "" } = useParams<{ slug?: string }>();

    const cmrAuth = useCmrAuth();
    const router = useHistory();

    const [categories, setCategories] = useState<Category[]>([]);
    const [activeCategory, setActiveCategory] = useState<Category | null>(null);
    const [scenes, setScenes] = useState<any[]>([]);
    const [expanded, setExpanded] = useState<Record<string, boolean>>({});

    const handleGoBack = () => {
        router.push({ pathname: RoutesName.preset });
    };

    const getCategories = async () => {
        try {
            const token = await cmrAuth.getAccessToken();

            const res = await fetch(`${process.env.REACT_APP_API_URL}/api/categories`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });

            const data = await res.json();
            setCategories(data.categories ?? []);
        } catch (err) {
            setCategories([]);
        }
    };

    useEffect(() => {
        getCategories();
    }, []);

    const flatCategories = useMemo(() => {
        const out: Category[] = [];

        const walk = (nodes: Category[]) => {
            for (const n of nodes) {
                out.push(n);
                if (n.children?.length) walk(n.children);
            }
        };

        walk(categories);

        return out;
    }, [categories]);

    useEffect(() => {
        if (!slug || !flatCategories.length) return;

        const category = flatCategories.find((c) => c.slug === slug);
        if (!category) return;

        setActiveCategory(category);

        const open: Record<string, boolean> = {};
        let current = "";

        for (const part of slug.split("/")) {
            current = current ? `${current}/${part}` : part;
            open[current] = true;
        }

        setExpanded(open);
    }, [slug, flatCategories]);

    const toggle = (slug: string) => {
        setExpanded((prev) => ({
            ...prev,
            [slug]: !prev[slug],
        }));
    };

    const handleClick = (category: Category) => {
        setActiveCategory(category);
        history.push(`/category/${category.slug}`);
    };

    const toggleNavigation = () => {
        document.querySelector(".category-list")?.classList.toggle("open");
    };

    useEffect(() => {
        if (!activeCategory) return;

        const fetchScenes = async () => {
            try {
                const token = await cmrAuth.getAccessToken();

                const res = await fetch(
                    `${process.env.REACT_APP_API_URL}/api/scenes/category/${activeCategory.id}`,

                    {
                        headers: {
                            Authorization: `Bearer ${token}`,
                        },
                    }
                );

                const data = await res.json();

                setScenes(data ?? []);
            } catch (err) {
                setScenes([]);
            }
        };

        fetchScenes();
    }, [activeCategory]);

    const renderTree = (nodes: Category[], depth = 0) => {
        return nodes.map((node) => {
            const hasChildren = node.children?.length > 0;
            const isOpen = !!expanded[node.slug];

            return (
                <React.Fragment key={node.id}>
                    <ListItemButton
                        selected={activeCategory?.id === node.id}
                        onClick={() => handleClick(node)}
                        sx={{ pl: 2 + depth * 2 }}
                    >
                        {hasChildren &&
                            (isOpen ? (
                                <ExpandLess
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        toggle(node.slug);
                                    }}
                                />
                            ) : (
                                <ExpandMore
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        toggle(node.slug);
                                    }}
                                />
                            ))}

                        <ListItemText
                            primary={
                                <Stack
                                    direction="row"
                                    spacing={1}
                                    justifyContent="space-between"
                                    alignItems="center"
                                >
                                    <span>{node.name}</span>

                                    <Chip
                                        label={node.totalSceneCount}
                                        size="small"
                                        color="primary"
                                    />
                                </Stack>
                            }
                        />
                    </ListItemButton>

                    {hasChildren && (
                        <Collapse in={isOpen} timeout="auto" unmountOnExit>
                            <List disablePadding>{renderTree(node.children, depth + 1)}</List>
                        </Collapse>
                    )}
                </React.Fragment>
            );
        });
    };

    return (
        <div className="fullWidthHeight presetPage noPadding">
            <Header hasMenu={true} />

            <div className="category-list">
                <List>
                    <ListItemButton
                        className="close"
                        onClick={() => toggleNavigation()}
                        sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                        }}
                    >
                        Categories <CloseIcon />
                    </ListItemButton>
                    {renderTree(categories)}
                </List>
            </div>

            <Container maxWidth={"lg"}>
                <div className="category-content" style={{ marginTop: "1rem" }}>
                    <section className="presetBody">
                        <Grid container>
                            {activeCategory ? (
                                <Grid item xs={12} className="heading">
                                    <div
                                        style={{
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "space-between",
                                        }}
                                    >
                                        <h1>{activeCategory.name}</h1>
                                        {scenes.length > 0 ? (
                                            <Chip
                                                label={`${scenes.length} setup${
                                                    scenes.length !== 1 ? "s" : ""
                                                }`}
                                                color="primary"
                                            />
                                        ) : (
                                            <Chip label="0 setups" color="primary" />
                                        )}
                                    </div>
                                </Grid>
                            ) : (
                                <Grid item xs={12} className="heading">
                                    <h3>Shared setups are grouped by category.</h3>
                                    <p>
                                        Select a category from the navigation to explore the
                                        available setups.
                                    </p>

                                    <div style={{ marginTop: "1rem" }}>
                                        <Grid container spacing={3}>
                                            {categories.map((category) => (
                                                <CategoryCard
                                                    key={category.id}
                                                    category={category}
                                                />
                                            ))}
                                        </Grid>
                                    </div>
                                </Grid>
                            )}

                            <CategoryBreadcrumbs slug={slug} categories={categories} />
                        </Grid>

                        {scenes.length > 0 ? (
                            <div style={{ marginTop: "1rem" }}>
                                <Grid container spacing={3}>
                                    {scenes.map((scene) => (
                                        <SetupCard
                                            key={scene.id}
                                            scene={scene}
                                            displayName={true}
                                        />
                                    ))}
                                </Grid>
                            </div>
                        ) : (
                            activeCategory &&
                            activeCategory.children.length > 0 && (
                                <Grid container spacing={3}>
                                    {activeCategory.children.map((child) => (
                                        <CategoryCard key={child.id} category={child} />
                                    ))}
                                </Grid>
                            )
                        )}
                    </section>
                </div>

                <section className="goBack">
                    <Grid container>
                        <Grid item xs={12}>
                            <Button
                                className="btnGoBack"
                                onClick={handleGoBack}
                                startIcon={<ArrowBackIcon className="iconArrow" />}
                            >
                                Back
                            </Button>
                        </Grid>
                    </Grid>
                </section>
            </Container>
            <Footer />
        </div>
    );
};

export default CategoryPage;
